/* ═══════════════════════════════════════════════════════════════════════════
   ZENSCRIPTSHOP — theme v1 · zen.js
   Vanilla, no dependencies, no build step.

   RULES THIS FILE OBEYS (do not break them when extending):
     1. Every module is wrapped in its own IIFE.
     2. Exactly ONE global: window.ZenTheme. Nothing else leaks.
     3. Every getElementById / querySelector result is null-guarded before use.
        (The previous theme shipped two copies of a global updateTime() with
        setInterval and no guard — 165+ console errors per page view. Never
        again: no setInterval anywhere in this file, no bare globals.)
     4. No loops, no timers driving visuals. Motion is one-shot only.

   ───────────────────────────────────────────────────────────────────────────
   MARKUP CONTRACTS SECTIONS MUST FOLLOW

   NAV
     <header class="nav">                          -> hairline on scroll
     <button id="nav-burger" class="nav-burger">   -> toggles
     <nav id="mobile-menu" class="mobile-menu">

   ANNOUNCEMENT
     <div class="announce" data-zen-announce> ... <button class="announce-x">

   CART BADGE
     any element with [data-cart-count] (also id="cart-count") gets item_count

   ADD TO CART
     <form action="/cart/add" method="post" data-zen-add-form>
       add data-zen-stay to stay on the page instead of going to /cart
       optional error slot inside the form, filled on a recoverable 4xx:
         <div data-zen-add-error role="alert" hidden></div>
       optional labels, read off the enclosing [data-zen-product-form] (or the
       form itself), each with a built-in fallback:
         data-adding-label  data-added-label  data-error-label

   PDP VARIANTS — every [data-zen-product-form] on the page is bound
   separately, so a featured-product block can coexist with a full PDP.
   Only the FIRST one writes ?variant= into the address bar.

   Option-pill mode (multi-option products)
     <form data-zen-product-form ...>
       <script type="application/json" data-zen-variants>
         [{ "id":123, "available":true, "options":["Xbox","30 days"],
            "price":"$35.00", "compare_at":null, "save":null }]
       </script>
       <input type="hidden" name="id" value="123" data-zen-variant-id>
       <label class="opt-value active">
         <input type="radio" data-zen-option data-index="0" name="option0" value="Xbox" checked>
         Xbox
       </label>
       <span data-zen-price></span> <s data-zen-compare></s> <span data-zen-save></span>
       a price node may wrap its number in <span data-zen-value> so a
       visually-hidden "Regular price" label survives the repaint
       <button data-zen-add>Add to cart</button>

   Direct-radio mode (single-option products) — one radio per variant.
   Takes precedence over the JSON above if both are present in a form.
     <label class="opt-value"><input type="radio" name="id" value="123"
        data-price="$35.00" data-compare="" data-save=""
        data-available="true" hidden> 30 days</label>

   QUANTITY
     <div class="qty"><button data-qty-minus>-</button>
       <input class="qty-input" data-qty-input name="quantity" value="1">
       <button data-qty-plus>+</button></div>

   PDP GALLERY
     <img data-zen-main src="...">   and   <img class="pdp-thumb" data-zen-thumb data-full="...">

   PDP STICKY BAR
     <div class="pdp-bar" id="pdp-bar"> ... <span data-zen-bar-price>

   FAQ
     <details class="faq-item"><summary class="faq-q">..</summary><div class="faq-a">..</div></details>

   CART PAGE
     <form data-zen-cart-form> with inputs [data-cart-qty] (auto-submit on change)

   SCROLL REVEAL
     add class="rv" yourself, or rely on the default selector list below
   ═══════════════════════════════════════════════════════════════════════════ */

window.ZenTheme = window.ZenTheme || {};

(function (Zen) {
  'use strict';

  /* ── shared helpers ────────────────────────────────────────────────────── */
  Zen.reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  Zen.$ = function (sel, root) {
    if (!sel) return null;
    try { return (root || document).querySelector(sel); } catch (e) { return null; }
  };
  Zen.$$ = function (sel, root) {
    if (!sel) return [];
    try { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
    catch (e) { return []; }
  };

  /* Update every cart badge on the page. Safe to call with no badges present. */
  Zen.setCartCount = function (n) {
    var count = Number(n) || 0;
    Zen.$$('[data-cart-count], #cart-count').forEach(function (el) {
      if (!el) return;
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
  };

  /* Re-read the real cart and repaint the badges. Never throws. */
  Zen.refreshCart = function () {
    if (!window.fetch) return Promise.resolve(null);
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart && typeof cart.item_count !== 'undefined') Zen.setCartCount(cart.item_count);
        return cart;
      })
      .catch(function () { return null; });
  };
})(window.ZenTheme);


/* ═══════════ NAV — hairline border once scrolled ═══════════ */
(function () {
  'use strict';
  var nav = document.querySelector('.nav');
  if (!nav) return;

  var on = false;
  var ticking = false;

  function apply() {
    ticking = false;
    var next = window.scrollY > 8;
    if (next !== on) { on = next; nav.classList.toggle('scrolled', next); }
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(apply);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  apply();
})();


/* ═══════════ MOBILE NAV ═══════════ */
(function () {
  'use strict';
  var burger = document.getElementById('nav-burger');
  var menu = document.getElementById('mobile-menu');
  if (!burger || !menu) return;

  function close() {
    menu.classList.remove('open');
    burger.classList.remove('open');
    burger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-open');
  }

  burger.addEventListener('click', function () {
    var open = menu.classList.toggle('open');
    burger.classList.toggle('open', open);
    burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.body.classList.toggle('menu-open', open);
  });

  menu.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('a')) close();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && menu.classList.contains('open')) close();
  });

  // Desktop breakpoint reclaims the layout — make sure state is clean.
  if (window.matchMedia) {
    var mq = window.matchMedia('(min-width: 860px)');
    var handler = function (ev) { if (ev.matches) close(); };
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
  }
})();


/* ═══════════ ANNOUNCEMENT BAR — dismissal persists until the copy changes ═══ */
(function () {
  'use strict';
  var bar = document.querySelector('[data-zen-announce]');
  if (!bar) return;

  var KEY = 'zen-announce-dismissed';
  var msg = (bar.textContent || '').replace(/\s+/g, ' ').trim();

  try {
    if (window.localStorage && localStorage.getItem(KEY) === msg) {
      bar.classList.add('hidden');
      return;
    }
  } catch (e) { /* storage blocked — just show the bar */ }

  var x = bar.querySelector('.announce-x');
  if (!x) return;
  x.addEventListener('click', function () {
    bar.classList.add('hidden');
    try { if (window.localStorage) localStorage.setItem(KEY, msg); } catch (e) {}
  });
})();


/* ═══════════ ADD TO CART ═══════════ */
(function () {
  'use strict';
  var Zen = window.ZenTheme;

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches) return;
    if (!form.matches('form[data-zen-add-form], form[action^="/cart/add"]')) return;
    if (!window.fetch || !window.FormData) return;   // let the browser do it

    e.preventDefault();

    // One add per form at a time. Disabling the button below is not enough:
    // the sticky mobile bar's submit button lives OUTSIDE the form (it is tied
    // to it with form="…"), so it stays clickable and a double tap used to post
    // /cart/add.js twice — two line items, and the second pass captured
    // "Adding…" as the label to restore.
    if (form.hasAttribute('data-zen-adding')) return;
    form.setAttribute('data-zen-adding', '1');

    var btn = form.querySelector('[data-zen-add], [name="add"]');
    var original = btn ? btn.textContent : '';

    // Every label the merchant can edit. They sit on the [data-zen-product-form]
    // wrapper alongside data-sold-out-label, so all four live in one place; a
    // bare /cart/add form with no wrapper falls back to the form itself, then
    // to these defaults.
    var labelHost = (form.closest && form.closest('[data-zen-product-form]')) || form;
    var addingLabel = labelHost.getAttribute('data-adding-label') || 'Adding…';
    var addedLabel  = labelHost.getAttribute('data-added-label')  || 'Added to cart';
    var errorLabel  = labelHost.getAttribute('data-error-label')  ||
                      'That could not be added right now. Please refresh the page and try again.';

    // Optional — a form with no error slot simply keeps the old behaviour of
    // silently re-enabling the button.
    var errorEl = form.querySelector('[data-zen-add-error]');

    function showError(message) {
      if (!errorEl) return;
      // Unhide first: a role="alert" region that is display:none is not in the
      // accessibility tree, so it has to exist before the text lands in it.
      errorEl.hidden = false;
      errorEl.textContent = message || errorLabel;
    }
    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
    function release() {
      form.removeAttribute('data-zen-adding');
      if (!btn) return;
      btn.disabled = false;
      btn.textContent = btn.dataset.labelIdle || original;
    }

    clearError();

    if (btn) {
      if (!btn.dataset.labelIdle) btn.dataset.labelIdle = original.trim();
      btn.disabled = true;
      btn.textContent = addingLabel;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) {
        if (r.ok) return r.json();
        // A 4xx here is RECOVERABLE and Shopify tells us why: sold out between
        // page load and click, quantity above stock, a rejected line-item
        // property. Re-posting the form natively fails the same way and dumps
        // the buyer on Shopify's bare /cart/add error page with no way back —
        // so this branch is kept well away from the offline fallback below.
        return r.json()
          .catch(function () { return null; })
          .then(function (data) {
            var err = new Error('cart-add-rejected');
            err.zenRejected = true;
            err.zenMessage = (data && (data.description || data.message)) || '';
            throw err;
          });
      })
      .then(function () { return Zen.refreshCart(); })
      .then(function () {
        if (form.hasAttribute('data-zen-stay')) {
          release();
          if (btn) btn.textContent = addedLabel;
          return;
        }
        window.location.href = '/cart';
      })
      .catch(function (err) {
        release();
        if (err && err.zenRejected) {
          showError(err.zenMessage);
          return;
        }
        form.submit();   // genuine fetch rejection (offline): let the browser post
      });
  });

  // bfcache restores can show a stale badge — and, because the page is frozen
  // mid-add on its way to /cart, a Back button press used to bring back a
  // permanently disabled buy button still reading "Adding…". Reset both.
  window.addEventListener('pageshow', function (ev) {
    if (!ev || !ev.persisted) return;
    Zen.$$('form[data-zen-adding]').forEach(function (f) {
      f.removeAttribute('data-zen-adding');
      var b = f.querySelector('[data-zen-add], [name="add"]');
      if (!b) return;
      b.disabled = false;
      if (b.dataset.labelIdle) b.textContent = b.dataset.labelIdle;
    });
    Zen.refreshCart();
  });
})();


/* ═══════════ PDP — variants, price, availability ═══════════ */
(function () {
  'use strict';
  var Zen = window.ZenTheme;

  // Every [data-zen-product-form] on the page is wired independently, so a
  // featured-product section can sit on the same page as a full PDP.
  var forms = Zen.$$('[data-zen-product-form]');
  if (!forms.length) return;
  forms.forEach(function (form, formIndex) { bind(form, formIndex === 0); });

function bind(form, isPrimary) {

  var priceEl   = form.querySelector('[data-zen-price]');
  var compareEl = form.querySelector('[data-zen-compare]');
  var saveEl    = form.querySelector('[data-zen-save]');
  var idInput   = form.querySelector('[data-zen-variant-id]');
  var addBtn    = form.querySelector('[data-zen-add]');
  var barPrice  = isPrimary ? document.querySelector('[data-zen-bar-price]') : null;
  // The sticky bar's submit button is outside the form, so setAvailable() has
  // to reach it explicitly or it will happily post a sold-out variant.
  var barBtn    = isPrimary ? document.querySelector('#pdp-bar [type="submit"]') : null;

  // The price block may live outside the <form> on a PDP layout.
  if (isPrimary) {
    if (!priceEl)   priceEl   = document.querySelector('[data-zen-price]');
    if (!compareEl) compareEl = document.querySelector('[data-zen-compare]');
    if (!saveEl)    saveEl    = document.querySelector('[data-zen-save]');
  }

  // A price node may carry a visually-hidden label ("Regular price") next to
  // its number, so the repaint writes into the [data-zen-value] child when one
  // exists instead of clobbering the label. No child = old behaviour exactly.
  function writePrice(el, value) {
    if (!el) return;
    var target = el.querySelector('[data-zen-value]') || el;
    target.textContent = value;
  }

  function paint(price, compare, save) {
    if (priceEl && price) writePrice(priceEl, price);
    if (barPrice && price) barPrice.textContent = price;
    if (compareEl) {
      writePrice(compareEl, compare || '');
      compareEl.hidden = !compare;
    }
    if (saveEl) {
      writePrice(saveEl, save || '');
      saveEl.hidden = !save;
    }
  }

  function setAvailable(available, soldOutLabel) {
    if (barBtn) barBtn.disabled = !available;
    if (!addBtn) return;
    addBtn.disabled = !available;
    if (!available) {
      if (!addBtn.dataset.labelOn) addBtn.dataset.labelOn = addBtn.textContent.trim();
      addBtn.textContent = soldOutLabel || addBtn.dataset.labelOff || 'Sold out';
    } else if (addBtn.dataset.labelOn) {
      addBtn.textContent = addBtn.dataset.labelOn;
    }
  }

  // Only the primary form owns the address bar — a featured-product block
  // must never rewrite ?variant= for the page it happens to sit on.
  function syncUrl(id) {
    if (!isPrimary || !id || !window.history || !history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      history.replaceState({}, '', url.toString());
    } catch (e) { /* older browsers: skip */ }
  }

  /* ── mode B: one radio per variant (name="id") ───────────────────────── */
  var directRadios = Zen.$$('input[name="id"][type="radio"]', form);
  if (directRadios.length) {
    form.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.name !== 'id' || input.type !== 'radio') return;

      Zen.$$('.opt-value', form).forEach(function (el) { el.classList.remove('active'); });
      var label = input.closest('.opt-value');
      if (label) label.classList.add('active');

      paint(input.getAttribute('data-price'), input.getAttribute('data-compare'), input.getAttribute('data-save'));
      setAvailable(input.getAttribute('data-available') !== 'false');
      syncUrl(input.value);
    });

    // Initial sync. Liquid draws the buy button from the PRODUCT's availability,
    // so a ?variant=<sold out> deep link on a product that still has other
    // variants in stock arrived with the sold-out radio checked and an enabled
    // "Add to cart" next to it. The option-pill path below already did this via
    // update(false); this branch returned without ever checking.
    var checkedRadio = form.querySelector('input[name="id"][type="radio"]:checked');
    if (checkedRadio) setAvailable(checkedRadio.getAttribute('data-available') !== 'false');
    return;
  }

  /* ── mode A: option pills matched against embedded variant JSON ──────── */
  var dataEl = form.querySelector('script[data-zen-variants]');
  if (!dataEl) return;

  var variants = [];
  try {
    var parsed = JSON.parse(dataEl.textContent || '[]');
    if (Array.isArray(parsed)) variants = parsed;
  } catch (e) { return; }
  if (!variants.length) return;

  var soldOutLabel = form.getAttribute('data-sold-out-label') || 'Sold out';
  var unavailableLabel = form.getAttribute('data-unavailable-label') || 'Unavailable';

  function optionInputs() {
    return Zen.$$('input[data-zen-option]', form);
  }

  function selection() {
    var out = [];
    optionInputs().forEach(function (input) {
      if (!input.checked) return;
      var i = parseInt(input.getAttribute('data-index'), 10);
      if (isNaN(i)) return;
      out[i] = input.value;
    });
    return out;
  }

  function matches(variant, sel) {
    var opts = variant && variant.options;
    if (!opts || !opts.length) return false;
    for (var i = 0; i < opts.length; i++) {
      if (typeof sel[i] !== 'undefined' && sel[i] !== null && sel[i] !== opts[i]) return false;
    }
    return true;
  }

  function findVariant(sel) {
    for (var i = 0; i < variants.length; i++) {
      if (matches(variants[i], sel)) return variants[i];
    }
    return null;
  }

  /* Grey out option values that can never be bought with the current picks. */
  function markAvailability(sel) {
    optionInputs().forEach(function (input) {
      var i = parseInt(input.getAttribute('data-index'), 10);
      if (isNaN(i)) return;
      var probe = sel.slice();
      probe[i] = input.value;
      var reachable = variants.some(function (v) { return v.available && matches(v, probe); });
      var label = input.closest('.opt-value');
      if (label) label.classList.toggle('unavailable', !reachable);
    });
  }

  function update(pushUrl) {
    var sel = selection();

    Zen.$$('.opt-value', form).forEach(function (label) {
      var input = label.querySelector('input[data-zen-option]');
      label.classList.toggle('active', !!(input && input.checked));
    });

    markAvailability(sel);

    var v = findVariant(sel);
    if (!v) {
      setAvailable(false, unavailableLabel);
      if (idInput) idInput.value = '';
      return;
    }

    if (idInput) idInput.value = v.id;
    paint(v.price, v.compare_at, v.save);
    setAvailable(v.available !== false, soldOutLabel);
    if (pushUrl) syncUrl(v.id);
  }

  form.addEventListener('change', function (e) {
    if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-zen-option')) update(true);
  });

  update(false);
}
})();


/* ═══════════ QUANTITY STEPPERS ═══════════ */
(function () {
  'use strict';
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-qty-minus], [data-qty-plus]') : null;
    if (!btn) return;

    var box = btn.closest('.qty');
    if (!box) return;
    var input = box.querySelector('[data-qty-input]');
    if (!input) return;

    e.preventDefault();
    var min = parseInt(input.getAttribute('min'), 10);
    if (isNaN(min)) min = 1;
    var max = parseInt(input.getAttribute('max'), 10);

    var value = parseInt(input.value, 10);
    if (isNaN(value)) value = min;
    value += btn.hasAttribute('data-qty-plus') ? 1 : -1;
    if (value < min) value = min;
    if (!isNaN(max) && value > max) value = max;

    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
})();


/* ═══════════ PDP GALLERY ═══════════ */
(function () {
  'use strict';
  var Zen = window.ZenTheme;

  var main = document.querySelector('[data-zen-main]');
  var thumbs = Zen.$$('[data-zen-thumb]');
  if (!main || !thumbs.length) return;

  thumbs.forEach(function (thumb) {
    thumb.addEventListener('click', function () {
      var full = thumb.getAttribute('data-full') || thumb.getAttribute('src');
      if (!full) return;
      main.setAttribute('src', full);
      if (thumb.hasAttribute('data-srcset')) main.setAttribute('srcset', thumb.getAttribute('data-srcset'));
      else main.removeAttribute('srcset');
      thumbs.forEach(function (t) { t.classList.remove('active'); });
      thumb.classList.add('active');
    });
  });
})();


/* ═══════════ PDP STICKY BUY BAR (mobile) ═══════════ */
(function () {
  'use strict';
  var bar = document.getElementById('pdp-bar');
  var anchor = document.querySelector('[data-zen-add]') || document.querySelector('.pdp-buy');
  if (!bar || !anchor) return;

  document.body.classList.add('has-pdp-bar');

  var ticking = false;
  function apply() {
    ticking = false;
    var r = anchor.getBoundingClientRect();
    bar.classList.toggle('show', r.bottom < 24);
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(apply);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  apply();
})();


/* ═══════════ CART PAGE — quantity changes re-submit the form ═══════════ */
(function () {
  'use strict';
  var form = document.querySelector('[data-zen-cart-form]');
  if (!form) return;

  var pending = null;
  form.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.hasAttribute || !input.hasAttribute('data-cart-qty')) return;
    if (pending) window.clearTimeout(pending);
    pending = window.setTimeout(function () {
      pending = null;
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    }, 220);
  });
})();


/* ═══════════ FAQ — smooth open/close on <details> ═══════════ */
(function () {
  'use strict';
  var Zen = window.ZenTheme;
  if (Zen.reduced) return;   // native instant toggle is fine

  Zen.$$('.faq-item').forEach(function (item) {
    var summary = item.querySelector('summary');
    var body = item.querySelector('.faq-a');
    if (!summary || !body) return;

    summary.addEventListener('click', function (e) {
      e.preventDefault();
      if (item.classList.contains('animating')) return;
      item.classList.add('animating');

      if (item.open) {
        var from = body.scrollHeight;
        body.style.height = from + 'px';
        window.requestAnimationFrame(function () {
          body.style.transition = 'height .26s cubic-bezier(.25,.6,.3,1), opacity .18s';
          body.style.height = '0px';
          body.style.opacity = '0';
          window.setTimeout(function () {
            item.open = false;
            body.style.cssText = '';
            item.classList.remove('animating');
          }, 280);
        });
      } else {
        item.open = true;
        var to = body.scrollHeight;
        body.style.height = '0px';
        body.style.opacity = '0';
        window.requestAnimationFrame(function () {
          body.style.transition = 'height .28s cubic-bezier(.25,.6,.3,1), opacity .22s .04s';
          body.style.height = to + 'px';
          body.style.opacity = '1';
          window.setTimeout(function () {
            body.style.cssText = '';
            item.classList.remove('animating');
          }, 320);
        });
      }
    });
  });
})();


/* ═══════════ SCROLL REVEAL — one subtle fade-up, then the observer detaches ═ */
(function () {
  'use strict';
  var Zen = window.ZenTheme;

  var SELECTOR = [
    '.gtile', '.pcard', '.review', '.feature', '.step',
    '.stat', '.trust-item', '.cta-card', '.table-card',
    '.media-split-fig', '.media-split-body', '.rating-summary',
    '.sec-head', '[data-zen-reveal]'
  ].join(', ');

  var els = Zen.$$(SELECTOR);
  if (!els.length) return;

  if (Zen.reduced || !('IntersectionObserver' in window)) {
    els.forEach(function (el) { el.classList.add('rv', 'in'); });
    return;
  }

  // The stagger is an inline transition-delay of up to 165ms. It has to stay
  // put until the reveal has actually run, but it must NOT survive it: .gtile
  // and .pcard both transition transform/border-color on hover, so a leftover
  // delay makes every hover on the game grid start late — roughly double the
  // 150-250ms the design calls for. Clearing it on transitionend keeps the
  // stagger intact and hands the element back at full speed afterwards.
  function reveal(el) {
    el.classList.add('in');
    var clear = function (ev) {
      if (ev && ev.target !== el) return;
      el.style.transitionDelay = '';
      el.removeEventListener('transitionend', clear);
    };
    el.addEventListener('transitionend', clear);
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      reveal(entry.target);
      io.unobserve(entry.target);
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -24px' });

  els.forEach(function (el, i) {
    el.classList.add('rv');
    el.style.transitionDelay = ((i % 4) * 55) + 'ms';
    io.observe(el);
  });

  // Safety net: if the observer never fires (throttled tab, exotic browser),
  // anything already on screen after load is revealed anyway. Runs once.
  window.addEventListener('load', function () {
    var h = window.innerHeight || document.documentElement.clientHeight;
    els.forEach(function (el) {
      if (el.classList.contains('in')) return;
      var r = el.getBoundingClientRect();
      if (r.top < h && r.bottom > 0) {
        reveal(el);
        io.unobserve(el);
      }
    });
  }, { once: true });
})();
